import React, { useReducer } from 'react';
import { Container, Table } from '@exxonmobil/react-unity';
import _ from 'lodash';
import styled from 'styled-components';
import { EndpointFooter } from './components/EndpointFooter';
import { ToolbarHeader } from './components/ToolbarHeader';
import { EndpointHead } from './components/EndpointHead';
import { EndpointBody } from './components/EndpointBody';
import { Column } from './types/Column';
import { COLUMNS } from './utils/columns';
import { EndpointData } from './types/EndpointData';
import {
  getTableData,
  getTotalConsumersForSelectedDomains,
  getTotalConsumptionVolumeForSelectedDomains
} from './utils/getTableData';
import { useEndpointsApi } from './hooks/useApi';

/* This page is display as utilization */
interface ColumnAction {
  type: string;
  columnName: string;
  value?: string;
  checked: boolean;
  columnsValues?: any;
}

const initialColumnState = COLUMNS.map((column) => ({
  ...column,
  hiddenValues: {}
}));

enum ColumnActionType {
  SET_COLUMN_CHECKED = 'SET_COLUMN_CHECKED',
  SET_VALUE_CHECKED = 'SET_VALUE_CHECKED'
}

const columnReducer = (state: Column[], action: ColumnAction) => {
  switch (action.type) {
    case ColumnActionType.SET_COLUMN_CHECKED:
      return state.map((column) => {
        if (column.name === action.columnName) {
          return {
            ...column,
            checked: action.checked
          };
        }
        return column;
      });
    case ColumnActionType.SET_VALUE_CHECKED:
      return state.map((column) => {
        if (column.name === action.columnName) {
          if (action.value === 'all') {
            const checkedAll = action.columnsValues[column.name].reduce((acc: {}, cur: string) => {
              return { ...acc, [cur]: !action.checked };
            }, {});
            return {
              ...column,
              hiddenValues: checkedAll
            };
          }
          return {
            ...column,
            hiddenValues: { ...column.hiddenValues, [action.value as string]: !action.checked }
          };
        }
        return column;
      });
    default:
      break;
  }
  return state;
};

const Endpoints = () => {
  const [columns, dispatch] = useReducer(columnReducer, initialColumnState);
  const { data: endpoints, error, isLoading, fetch: fetchEndpoints } = useEndpointsApi();

  const data: EndpointData[] = getTableData(endpoints ?? [], columns) as EndpointData[];
  const domainCount = data.length;

  const endpointCount = _(data)
    .map((domain) => domain.rows)
    .flatten()
    .value().length;

  const columnsValues = _(COLUMNS)
    .map((column) => {
      const values = _(endpoints ?? [])
        .map((domain) => column.getValues(domain))
        .flattenDeep()
        .uniq()
        .value();
      return [column.name, ['all', ...values]];
    })
    .fromPairs()
    .value();

  const volumeCount = getTotalConsumptionVolumeForSelectedDomains(data ?? []);
  const consumerCount = getTotalConsumersForSelectedDomains(data ?? []);

  return (
    <StyledContainer>
      <Table
        id="endpoint-table"
        striped
        style={{ border: '1px solid #e5e5e5', marginTop: '2rem' }}
        header={
          <ToolbarHeader
            columns={columns}
            data={data}
            onChangeFilter={(columnName: string, checked: boolean) =>
              dispatch({ type: ColumnActionType.SET_COLUMN_CHECKED, columnName, checked })
            }
          />
        }
        footer={
          <EndpointFooter
            domainCount={domainCount}
            endpointCount={endpointCount}
            consumerCount={consumerCount}
            volumeCount={volumeCount}
          />
        }
      >
        <EndpointHead
          columns={columns}
          columnValues={columnsValues}
          onChangeFilter={(columnName: string, value: string, checked: boolean) =>
            dispatch({ type: ColumnActionType.SET_VALUE_CHECKED, columnName, value, checked, columnsValues })
          }
        />
        <EndpointBody isLoading={isLoading} data={data} error={error} onRefresh={fetchEndpoints} />
      </Table>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  & .em-c-table-object__body-inner {
    display: 'relative';
    min-height: calc(100vh - 280px);
  }
`;

export default Endpoints;
